import ActionResult from 'app/src/engine/action-results/ActionResult';
import GearTileActionResult, {
	GearTileActionResultType,
} from 'app/src/engine/action-results/GearTileActionResult';
import MoveMovableItemActionResult from 'app/src/engine/action-results/MoveMovableItemActionResult';
import MovableItem, {
	MovableItemLocation,
	MovableItemRotationAmount,
	rotateMovableItemOrientation,
	turnMovableItem,
} from 'app/src/engine/movable-items/MovableItem';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import GearTile, { GearTileDirection } from 'app/src/engine/tiles/GearTile';
import GameStateStore from 'app/src/game/GameStateStore';

export default class GearTileInteraction extends TileInteraction {
	get priority(): number {
		return 1000;
	}

	private _finishedExecuting = false;
	get finishedExecuting(): boolean {
		return this._finishedExecuting;
	}

	public reset(): void {
		this._finishedExecuting = false;
	}

	public _executeSegment(gameStateStore: GameStateStore): ActionResult[] {
		const actionResults: ActionResult[] = [];

		// Iterate over each tile.
		for (let row = 0; row < gameStateStore.rows; row++) {
			for (let col = 0; col < gameStateStore.cols; col++) {
				// We only care about GearTiles.
				const tile = gameStateStore.getTile(row, col);
				if (!(tile instanceof GearTile)) {
					continue;
				}

				// Check if movable items exist on this tile.
				let targetMovableItem: MovableItem | null = null;
				let targetMovableItemLocation: MovableItemLocation | null = null;

				for (const movableItem of gameStateStore.movableItems) {
					targetMovableItemLocation = gameStateStore.movableItemLocations.get(
						movableItem,
					) as MovableItemLocation;
					if (targetMovableItemLocation!.row === row && targetMovableItemLocation!.col === col) {
						targetMovableItem = movableItem;
					}
				}

				// If there's no movable items on this tile, skip it.
				if (!targetMovableItem || !targetMovableItemLocation) {
					continue;
				}

				// Add a GearTileActionResult.
				const gearTileActionResultType =
					tile.direction === GearTileDirection.Clockwise
						? GearTileActionResultType.RotateClockwise
						: GearTileActionResultType.RotateCounterClockwise;
				const gearTileActionResult = new GearTileActionResult(
					tile,
					{ row, col },
					gearTileActionResultType,
				);
				actionResults.push(gearTileActionResult);

				// Add a MoveMovableItemActionResult.
				const moveMovableItemActionResult = new MoveMovableItemActionResult(
					targetMovableItem,
					targetMovableItemLocation,
				);
				moveMovableItemActionResult.newLocation = targetMovableItemLocation;
				moveMovableItemActionResult.newLocation.turns = turnMovableItem(
					moveMovableItemActionResult.newLocation.turns,
					tile.direction === GearTileDirection.Clockwise
						? MovableItemRotationAmount.TurnRight
						: MovableItemRotationAmount.TurnLeft,
				);
				moveMovableItemActionResult.newLocation.orientation = rotateMovableItemOrientation(
					moveMovableItemActionResult.newLocation.orientation,
					tile.direction === GearTileDirection.Clockwise
						? MovableItemRotationAmount.TurnRight
						: MovableItemRotationAmount.TurnLeft,
				);
				actionResults.push(moveMovableItemActionResult);
			}
		}

		// Execute all final action results.
		for (const actionResult of actionResults) {
			actionResult.execute(gameStateStore);
		}

		// This interaction is finished until it's reset.
		this._finishedExecuting = true;

		return actionResults;
	}
}
